/* eslint-disable react-hooks/exhaustive-deps */
import { KTSVG } from '../../_metronic/helpers';
import React, { useState, useEffect, useRef } from 'react';
import { Table } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Link, useHistory } from 'react-router-dom';
import validationErrorMsg from '../helpers/ValidationErrorMsg';

interface AdjustmentRow {
    idItems: number;
    itemName: string;
    itemCode: string;
    categoryName: string;
    unitName: string;
    stockQuantity: number;
    physicalQuantity: number;
    averageRate: number;
    [key: string]: any;
}

interface AdjustmentData {
    idProjects: number;
    idWarehouses: number;
    idItemStockAdjustmentType: number;
    adjustmentDate: string;
    remarks: string;
}

const ItemStockAdjustmentCreate = () => {
    const history = useHistory();
    const today = new Date().toISOString().split('T')[0];

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState<any>({ label: 'Select Project', value: '' });
    const [warehouses, setWarehouses] = useState<any[]>([]);
    const [warehouse, setWarehouse] = useState<any>({ label: 'Select Warehouse', value: '' });
    const [adjustmentTypes, setAdjustmentTypes] = useState<any[]>([]);
    const [adjustmentType, setAdjustmentType] = useState<any>({ label: 'Select Adjustment Type', value: '' });

    const [adjustment, setAdjustment] = useState<AdjustmentData>({
        idProjects: 0,
        idWarehouses: 0,
        idItemStockAdjustmentType: 0,
        adjustmentDate: today,
        remarks: '',
    });

    const [adjustmentRows, setAdjustmentRows] = useState<AdjustmentRow[]>([
        { idItems: 0, itemName: '', itemCode: '', categoryName: '', unitName: '', stockQuantity: 0, physicalQuantity: 0, averageRate: 0 },
    ]);

    // Ref to capture latest input (fixes paste not triggering backend search - loadOptions can receive stale inputValue)
    const itemSearchInputRef = useRef('');

    useEffect(() => {
        getProjects();
        getAdjustmentTypes();
    }, []);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.status && resp.data) {
                    setProjects(resp.data);
                }
            })
            .catch(console.error);
    };

    const getWarehousesByProject = (idProjects: number) => {
        if (!idProjects) {
            setWarehouses([]);
            setWarehouse({ label: 'Select Warehouse', value: '' });
            return;
        }
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/warehouses/' + idProjects, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.status && resp.data) {
                    setWarehouses(resp.data);
                } else {
                    setWarehouses([]);
                }
                setWarehouse({ label: 'Select Warehouse', value: '' });
            })
            .catch(() => setWarehouses([]));
    };

    const getAdjustmentTypes = () => {
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/adjustment-types', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.status && resp.data) {
                    setAdjustmentTypes(resp.data);
                }
            })
            .catch(console.error);
    };

    const loadItemOptions = (inputValue: string): Promise<any[]> => {
        if (!project?.value || !warehouse?.value || !adjustment.adjustmentDate) {
            return Promise.resolve([]);
        }
        // Use ref as fallback so paste/rapid input triggers search with correct value (react-select can pass stale inputValue)
        const searchTerm = (inputValue || itemSearchInputRef.current || '').trim();
        const params = new URLSearchParams({
            id_projects: String(project.value),
            id_warehouses: String(warehouse.value),
            adjustment_date: adjustment.adjustmentDate,
            search: searchTerm,
        });
        return fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/items-with-stock?' + params, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => (resp.status && resp.data ? resp.data : []))
            .catch(() => []);
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setAdjustment((prev) => ({ ...prev, idProjects: selected?.value || 0 }));
        getWarehousesByProject(selected?.value);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        setAdjustmentRows([{ idItems: 0, itemName: '', itemCode: '', unitName: '', stockQuantity: 0, physicalQuantity: 0, averageRate: 0 }]);
    };

    const handleWarehouseChange = (selected: any) => {
        setWarehouse(selected);
        setAdjustment((prev) => ({ ...prev, idWarehouses: selected?.value || 0 }));
        setAdjustmentRows([{ idItems: 0, itemName: '', itemCode: '', unitName: '', stockQuantity: 0, physicalQuantity: 0, averageRate: 0 }]);
    };

    const handleAdjustmentTypeChange = (selected: any) => {
        setAdjustmentType(selected);
        setAdjustment((prev) => ({ ...prev, idItemStockAdjustmentType: selected?.value || 0 }));
    };

    const handleItemSelect = (option: any, index: number) => {
        if (!option) return;
        const updated = [...adjustmentRows];
        updated[index] = {
            idItems: option.idItems,
            itemName: option.itemName,
            itemCode: option.itemCode,
            categoryName: option.categoryName ?? '',
            unitName: option.unitName,
            stockQuantity: option.stockQuantity ?? 0,
            physicalQuantity: option.stockQuantity ?? 0,
            averageRate: option.averageRate ?? 0,
        };
        setAdjustmentRows(updated);
    };

    const handlePhysicalQtyChange = (index: number, value: number) => {
        const updated = [...adjustmentRows];
        updated[index].physicalQuantity = value;
        setAdjustmentRows(updated);
    };

    const addRow = () => {
        setAdjustmentRows([
            ...adjustmentRows,
            { idItems: 0, itemName: '', itemCode: '', categoryName: '', unitName: '', stockQuantity: 0, physicalQuantity: 0, averageRate: 0 },
        ]);
    };

    const removeRow = (index: number) => {
        if (adjustmentRows.length <= 1) return;
        const updated = adjustmentRows.filter((_, i) => i !== index);
        setAdjustmentRows(updated);
    };

    const getAdjustmentQty = (row: AdjustmentRow) => {
        const phys = Number(row.physicalQuantity) || 0;
        const prev = Number(row.stockQuantity) || 0;
        return parseFloat((phys - prev).toFixed(2));
    };

    const getValidRows = () => {
        return adjustmentRows.filter((r) => r.idItems);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!adjustment.idProjects) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Project.' });
            return;
        }
        if (!adjustment.idWarehouses) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Warehouse.' });
            return;
        }
        if (!adjustment.idItemStockAdjustmentType) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Adjustment Type.' });
            return;
        }
        if (!adjustment.adjustmentDate) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Date.' });
            return;
        }

        const validRows = getValidRows();
        if (validRows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Add at least one item.',
            });
            return;
        }

        const confirm = await Swal.fire({
            title: 'Confirm',
            text: 'Save this stock adjustment?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3699FF',
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Yes, Adjust',
        });

        if (!confirm.isConfirmed) return;

        const postData = {
            adjustment: {
                idProjects: adjustment.idProjects,
                idWarehouses: adjustment.idWarehouses,
                idItemStockAdjustmentType: adjustment.idItemStockAdjustmentType,
                adjustmentDate: adjustment.adjustmentDate,
                remarks: adjustment.remarks || null,
            },
            adjustmentRows: validRows.map((r) => ({
                idItems: r.idItems,
                stockQuantity: isNaN(Number(r.stockQuantity)) ? 0 : Number(r.stockQuantity),
                physicalQuantity: Number(r.physicalQuantity) >= 0 ? Number(r.physicalQuantity) : 0,
                averageRate: Number(r.averageRate) >= 0 ? Number(r.averageRate) : 0,
            })),
        };

        try {
            const res = await fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment', postRequestOptions(postData));
            const data = await res.json();

            if (data.success) {
                Swal.fire({ icon: 'success', title: 'Success', text: data.successMessage || 'Stock adjustment saved.' });
                history.push('/item-stock-adjustment');
            } else {
                const msg = validationErrorMsg(data.errors).join('<br>') || data.errorMessage || 'Failed to save.';
                Swal.fire({ icon: 'error', title: 'Error', html: msg });
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Request failed.' });
        }
    };

    return (
        <div className="card card-custom">
            <div className="card-header">
                <div className="card-title">
                    <KTSVG path='/media/svg/icons/Home/Book-open.svg' className='svg-icon-1 svg-icon-primary' />
                    <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                        Stock Adjustment
                    </span>
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Date <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={adjustment.adjustmentDate}
                                onChange={(e) => {
                                    setAdjustment((p) => ({ ...p, adjustmentDate: e.target.value }));
                                    setAdjustmentRows([{ idItems: 0, itemName: '', itemCode: '', categoryName: '', unitName: '', stockQuantity: 0, physicalQuantity: 0, averageRate: 0 }]);
                                }}
                                required
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Project <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select
                                value={project}
                                onChange={handleProjectChange}
                                options={projects}
                                placeholder="Select Project"
                                menuPosition="fixed"
                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Warehouse <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select
                                value={warehouse}
                                onChange={handleWarehouseChange}
                                options={warehouses}
                                placeholder="Select Warehouse"
                                menuPosition="fixed"
                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                isDisabled={!project?.value}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Adjustment Type <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select
                                value={adjustmentType}
                                onChange={handleAdjustmentTypeChange}
                                options={adjustmentTypes}
                                placeholder="Select Adjustment Type"
                                menuPosition="fixed"
                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Remarks</label>
                        <div className="col-lg-4">
                            <textarea
                                className="form-control form-control-sm"
                                rows={2}
                                value={adjustment.remarks}
                                onChange={(e) => setAdjustment((p) => ({ ...p, remarks: e.target.value }))}
                            />
                        </div>
                    </div>

                    <h6 className="mb-2">Material Details</h6>
                    <Table size="sm" bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '4%' }} className="text-center">SL</th>
                                <th style={{ width: '22%' }}>Name of Materials</th>
                                <th style={{ width: '12%' }}>Item Code</th>
                                <th style={{ width: '12%' }}>Category</th>
                                <th style={{ width: '8%' }}>Unit</th>
                                <th style={{ width: '10%' }}>Avg. Rate</th>
                                <th style={{ width: '10%' }}>Prev. Stock</th>
                                <th style={{ width: '12%' }}>Physical Qty</th>
                                <th style={{ width: '12%' }}>Adjustment Qty</th>
                                <th style={{ width: '6%' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adjustmentRows.map((row, index) => (
                                <tr key={index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>
                                        <AsyncSelect
                                            cacheOptions={false}
                                            defaultOptions
                                            loadOptions={loadItemOptions}
                                            onInputChange={(val) => { itemSearchInputRef.current = val ?? ''; }}
                                            value={row.idItems ? { value: row.idItems, label: row.itemName + (row.categoryName ? ' - ' + row.categoryName : ''), ...row } : null}
                                            onChange={(opt) => handleItemSelect(opt, index)}
                                            placeholder="Search item by name or code..."
                                            menuPosition="fixed"
                                            styles={{
                                                control: (p: any) => ({ ...p, minHeight: '32px' }),
                                                container: (p: any) => ({ ...p, minHeight: '32px' }),
                                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                            }}
                                            menuPortalTarget={document.body}
                                            isDisabled={!project?.value || !warehouse?.value}
                                            noOptionsMessage={() => (!project?.value || !warehouse?.value ? 'Select Project and Warehouse first' : 'Type to search items...')}
                                        />
                                    </td>
                                    <td><input type="text" className="form-control form-control-sm" value={row.itemCode} readOnly /></td>
                                    <td><input type="text" className="form-control form-control-sm" value={row.categoryName || ''} readOnly /></td>
                                    <td><input type="text" className="form-control form-control-sm" value={row.unitName} readOnly /></td>
                                    <td><input type="number" className="form-control form-control-sm" value={row.averageRate} readOnly step="any" /></td>
                                    <td><input type="number" className="form-control form-control-sm" value={row.stockQuantity} readOnly step="any" /></td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={row.physicalQuantity === 0 || row.physicalQuantity ? row.physicalQuantity : ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const num = val === '' ? 0 : parseFloat(val);
                                                handlePhysicalQtyChange(index, isNaN(num) ? 0 : Math.max(0, num));
                                            }}
                                            min={0}
                                            step="any"
                                            placeholder="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${getAdjustmentQty(row) !== 0 ? 'fw-bold' : ''}`}
                                            value={getAdjustmentQty(row)}
                                            readOnly
                                            style={{ color: getAdjustmentQty(row) > 0 ? '#28a745' : getAdjustmentQty(row) < 0 ? '#dc3545' : '' }}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-light-danger btn-sm"
                                            onClick={() => removeRow(index)}
                                            disabled={adjustmentRows.length <= 1}
                                        >
                                            <KTSVG path='/media/svg/icons/General/Trash.svg' className='svg-icon-5' />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <button type="button" className="btn btn-light-primary btn-sm" onClick={addRow}>
                            <KTSVG path='/media/svg/icons/Navigation/Plus.svg' className='svg-icon-5 me-1' />
                            Add New
                        </button>
                        <div>
                            <button type="submit" className="btn btn-primary btn-sm me-2">
                                Adjust
                            </button>
                            <Link to="/item-stock-adjustment" className="btn btn-secondary btn-sm">
                                Cancel
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ItemStockAdjustmentCreate;
